import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, makeAuthenticatedRequest } from '../services/authService';
import { habitService, Habit, HabitCompletion } from '../services/habitService';
import { goalService } from '../services/goalService';
import { Goal } from '../types/Goal';
import { taskService, Task } from '../services/taskService';
import DailyNotesService, { DailyNote } from '../services/dailyNotesService';
import { NotesService, Note } from '../services/notesService';
import { ContentBlock } from '../services/blockService';
import BlockEditor from './notes/BlockEditor';
import { useBlockManager } from '../hooks/useBlockManager';
import { BlockContainer } from '../services/blockManager';
import PageHeader from './common/PageHeader';
import { cashflowService, CashflowEntry } from '../services/cashflowService';
import { getActivityCounts } from '../services/activityService';
import { TimeLogsService } from '../services/timelogsService';
import { stockPortfolioService, StockTransaction } from '../services/stockPortfolioService';
import './Weeks.css';

interface WeeksProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface WeeklyStats {
  habits: {
    total: number;
    completed: number;
    completionRate: number;
    habits: Habit[];
  };
  goals: {
    total: number;
    active: number;
    completed: number;
    goals: Goal[];
  };
  tasks: {
    total: number;
    completed: number;
    completionRate: number;
    tasks: Task[];
  };
  notes: {
    totalDays: number;
    daysWithNotes: number;
    notes: DailyNote[];
  };
  cashflow: {
    totalSpent: number;
    totalIncome: number;
    netCashflow: number;
    transactions: number;
  };
  blocks: {
    total: number;
  };
  lessons: {
    total: number;
  };
  activities: {
    total: number;
  };
  books: {
    totalPages: number;
    recordCount: number;
  };
  meditation: {
    totalMinutes: number;
    totalHours: number;
    sessionCount: number;
  };
  stocks: {
    buyCount: number;
    sellCount: number;
    totalTransactions: number;
    transactions: StockTransaction[];
  };
}

const Weeks: React.FC<WeeksProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { yearWeek } = useParams<{ yearWeek?: string }>();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(0); // 0 = current week, -1 = last week, etc.
  
  // Weekly note state
  const [weeklyNote, setWeeklyNote] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs to prevent duplicate loading calls
  const loadingDataRef = useRef<boolean>(false);
  const loadingNoteRef = useRef<boolean>(false);
  
  // Create a ref to hold the current weekly note for the callback
  const weeklyNoteRef = useRef<Note | null>(null);
  
  // Update the ref whenever weeklyNote changes
  useEffect(() => {
    weeklyNoteRef.current = weeklyNote;
  }, [weeklyNote]);

  // Utility functions for year-week conversion
  const getISOWeekNumber = (date: Date): { year: number; week: number } => {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    const weekNumber = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
    return { year: target.getFullYear(), week: weekNumber };
  };

  const getDateFromYearWeek = (year: number, week: number): Date => {
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay() || 7;
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - jan4Day + 1);
    const targetDate = new Date(mondayOfWeek1);
    targetDate.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);
    return targetDate;
  };

  const formatYearWeek = (date: Date): string => {
    const { year, week } = getISOWeekNumber(date);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  };

  const parseYearWeek = (yearWeekStr: string): { year: number; week: number } | null => {
    const match = yearWeekStr.match(/^(\d{4})-W(\d{1,2})$/);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const week = parseInt(match[2], 10);
    if (week < 1 || week > 53) return null;
    return { year, week };
  };

  const getCurrentYearWeek = (): string => {
    return formatYearWeek(new Date());
  };

  const getWeekOffsetFromYearWeek = (yearWeekStr: string): number => {
    if (yearWeekStr === 'current') return 0;
    
    const parsed = parseYearWeek(yearWeekStr);
    if (!parsed) return 0;
    
    const targetDate = getDateFromYearWeek(parsed.year, parsed.week);
    const currentDate = new Date();
    const currentWeekStart = new Date(currentDate);
    // ISO week starts on Monday (1), so convert Sunday (0) to 7
    const dayOfWeek = (currentDate.getDay() + 6) % 7; // Monday = 0, Sunday = 6
    currentWeekStart.setDate(currentDate.getDate() - dayOfWeek);
    
    const diffTime = targetDate.getTime() - currentWeekStart.getTime();
    const diffWeeks = Math.round(diffTime / (7 * 24 * 60 * 60 * 1000));
    return diffWeeks;
  };

  const getYearWeekFromOffset = (offset: number): string => {
    const currentDate = new Date();
    // Get the Monday of the current week
    const currentWeekStart = new Date(currentDate);
    const dayOfWeek = (currentDate.getDay() + 6) % 7; // Monday = 0, Sunday = 6
    currentWeekStart.setDate(currentDate.getDate() - dayOfWeek);
    
    // Add the offset in weeks
    const targetDate = new Date(currentWeekStart);
    targetDate.setDate(currentWeekStart.getDate() + (offset * 7));
    return formatYearWeek(targetDate);
  };

  // Get week dates for a given week offset (ISO week - Monday-based)
  const getWeekDates = (weekOffset: number = 0): Date[] => {
    // For simplicity, use the existing getDateFromYearWeek with current date + offset
    const currentDate = new Date();
    const targetDate = new Date(currentDate);
    targetDate.setDate(currentDate.getDate() + (weekOffset * 7));
    
    // Get the ISO week for the target date
    const { year, week } = getISOWeekNumber(targetDate);
    
    return getWeekDatesFromYearWeek(year, week);
  };

  // Helper function to get week dates from year-week
  const getWeekDatesFromYearWeek = (year: number, week: number): Date[] => {
    const mondayDate = getDateFromYearWeek(year, week);
    const weekDates: Date[] = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(mondayDate);
      day.setDate(mondayDate.getDate() + i);
      weekDates.push(day);
    }
    return weekDates;
  };

  // Format date for API calls
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Generate week range string for note ID
  const getWeekRangeId = (weekOffset: number = 0): string => {
    const weekDates = getWeekDates(weekOffset);
    const startDate = formatDate(weekDates[0]);
    const endDate = formatDate(weekDates[6]);
    return `week-${startDate}-to-${endDate}`;
  };

  // Generate week display range
  const getWeekDisplayRange = (weekOffset: number = 0): string => {
    const weekDates = getWeekDates(weekOffset);
    const startDate = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDate = weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  };

  // Block management for weekly note
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'note',
    containerId: weeklyNote?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const weeklyNoteValue = weeklyNoteRef.current;
      
      console.log('🔧 Updating weekly note:', container);
      console.log('🔧 Weekly note:', weeklyNoteValue);
      
      if (weeklyNoteValue) {
        const blocks = container.blocks.map((blockId, index) => ({
          block_id: blockId,
          order: index
        }));

        await NotesService.updateNote(weeklyNoteValue.note_id, {
          title: weeklyNoteValue.title,
          blocks,
          properties: weeklyNoteValue.properties
        });
      }
    }
  });

  // Load or create weekly note
  const loadWeeklyNote = useCallback(async (weekOffset: number = 0) => {
    if (!user?.id || loadingNoteRef.current) return;

    loadingNoteRef.current = true;
    setNoteLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const weekRangeId = getWeekRangeId(weekOffset);
      const weekDisplayRange = getWeekDisplayRange(weekOffset);

      // console.log('🔧 Loading weekly note:', { weekRangeId, weekDisplayRange });

      try {
        // Try to load existing note
        const existingNote = await NotesService.getNoteById(weekRangeId, userId);
        setWeeklyNote(existingNote);
        setContainerId(existingNote.note_id);

        // Load the content blocks
        const blockIds = existingNote.blocks.map(b => b.block_id);
        // console.log('🔧 About to load blocks:', { blockIds, hasBlockManager: !!blockManager });
        await loadBlocks(blockIds);

        // console.log('✅ Loaded existing weekly note:', existingNote.note_id);
      } catch (error: any) {
        // If note doesn't exist, create it
        if (error.status === 404 || error.message?.includes('not found')) {
          // console.log('📝 Creating new weekly note:', weekRangeId);
          
          const newNote = await NotesService.createNoteWithId(
            userId,
            weekRangeId,
            `Week: ${weekDisplayRange}`,
            []
          );
          
          setWeeklyNote(newNote);
          setContainerId(newNote.note_id);
          
          // console.log('✅ Created new weekly note:', newNote.note_id);
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Error loading weekly note:', error);
      setError('Failed to load weekly note');
    } finally {
      loadingNoteRef.current = false;
      setNoteLoading(false);
    }
  }, [user?.id, loadBlocks, setContainerId]);

  // Load weekly data
  const loadWeeklyData = useCallback(async (weekOffset: number = 0) => {
    if (!user?.id || loadingDataRef.current) return;

    try {
      loadingDataRef.current = true;
      setLoading(true);
      setError(null);

      const userId = getUserId();
      const weekDates = getWeekDates(weekOffset);
      const startDate = formatDate(weekDates[0]);
      const endDate = formatDate(weekDates[weekDates.length - 1]);

      // Load all data in parallel
      const [
        habitsResponse,
        completionsResponse,
        goalsResponse,
        tasksResponse,
        notesResponse,
        cashflowResponse,
        blocksResponse,
        lessonsResponse,
        activitiesResponse,
        booksResponse,
        meditationResponse,
        stockTransactionsResponse
      ] = await Promise.all([
        habitService.getUserHabits(userId, { is_active: true }),
        habitService.getCompletionsByDateRange(userId, startDate, endDate),
        goalService.getUserGoals({ user_id: userId.toString() }),
        taskService.getUserTasks({ user_id: userId.toString() }),
        DailyNotesService.getNotesInRange(userId, startDate, endDate),
        cashflowService.getCashflowEntries({
          start_date: startDate,
          end_date: endDate,
          limit: 10000,
          offset: 0
        }),
        makeAuthenticatedRequest(
          `/blocks/user/${userId}/counts?start_date=${startDate}&end_date=${endDate}&group_by=week`,
          { method: 'GET' }
        ).catch(() => ({ success: false, data: [] })),
        makeAuthenticatedRequest(
          `/lessons/counts?start_date=${startDate}&end_date=${endDate}&group_by=week`,
          { method: 'GET' }
        ).catch(() => ({ success: false, counts: [] })),
        getActivityCounts(startDate, endDate).catch(() => ({ success: false, counts_by_date: {}, total_count: 0 })),
        makeAuthenticatedRequest(
          `/books/user/reading-stats?start_date=${startDate}&end_date=${endDate}`,
          { method: 'GET' }
        ).catch(() => ({ success: false, total_pages: 0, record_count: 0 })),
        TimeLogsService.getTimeLogsByActivityType(userId, 'meditation', startDate, endDate).catch(() => []),
        stockPortfolioService.getUserTransactions(userId.toString(), undefined, 10000).catch(() => ({ success: false, transactions: [] }))
      ]);

      // Process habits data
      const habits = habitsResponse.success && habitsResponse.habits ? habitsResponse.habits : [];
      const completionsByDate = completionsResponse.success && completionsResponse.completions_by_date 
        ? completionsResponse.completions_by_date 
        : {};

      let totalHabitCompletions = 0;
      let totalPossibleCompletions = 0;

      habits.forEach(habit => {
        weekDates.forEach(date => {
          const dateStr = formatDate(date);
          const dayCompletions = completionsByDate[dateStr] || [];
          const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habit.id));
          
          totalPossibleCompletions++;
          if (habitCompletion && habitCompletion.is_completed) {
            totalHabitCompletions++;
          }
        });
      });

      // Process goals data
      const goals = goalsResponse.success && goalsResponse.goals ? goalsResponse.goals : [];
      const activeGoals = goals.filter(goal => goal.status === 'active');
      const completedGoals = goals.filter(goal => goal.status === 'completed');

      // Process tasks data
      // Count tasks created in the week and tasks completed in the week (using timestamps)
      const allTasks = tasksResponse.success && tasksResponse.tasks ? tasksResponse.tasks : [];
      
      // Get date strings for comparison (YYYY-MM-DD format)
      const startDateStr = formatDate(weekDates[0]);
      const endDateStr = formatDate(weekDates[6]);
      
      // Tasks created during the week
      const tasksCreatedInWeek = allTasks.filter(task => {
        if (!task.created_at) return false;
        const createdDateStr = formatDate(new Date(task.created_at));
        return createdDateStr >= startDateStr && createdDateStr <= endDateStr;
      });
      
      // Tasks completed during the week (using completed_at timestamp)
      const tasksCompletedInWeek = allTasks.filter(task => {
        if (!task.completed_at || task.status !== 'completed') return false;
        const completedDateStr = formatDate(new Date(task.completed_at));
        return completedDateStr >= startDateStr && completedDateStr <= endDateStr;
      });
      
      const taskCompletionRate = tasksCreatedInWeek.length > 0 ? (tasksCompletedInWeek.length / tasksCreatedInWeek.length) * 100 : 0;

      // Process notes data
      const notes = Array.isArray(notesResponse) ? notesResponse : [];
      const daysWithNotes = notes.length;
      const totalDays = 7;

      // Process cashflow data
      const cashflowEntries = cashflowResponse.success && cashflowResponse.cashflow ? cashflowResponse.cashflow : [];
      // Calculate spending: exclude income and investment categories, use absolute value
      const totalSpent = cashflowEntries
        .filter(entry => entry.category !== 'income' && entry.category !== 'investment')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      // Calculate income: only entries with income category
      const totalIncome = cashflowEntries
        .filter(entry => entry.category === 'income')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      const netCashflow = totalIncome - totalSpent;

      // Process blocks data
      const blocksData = blocksResponse.success && blocksResponse.data ? blocksResponse.data : [];
      const totalBlocks = blocksData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process lessons data
      const lessonsData = lessonsResponse.success && lessonsResponse.counts ? lessonsResponse.counts : [];
      const totalLessons = lessonsData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process activities data
      const activitiesCounts = activitiesResponse.success && activitiesResponse.counts_by_date ? activitiesResponse.counts_by_date : {};
      // Use total_count if available, otherwise sum daily counts
      const totalActivities = activitiesResponse.total_count !== undefined 
        ? activitiesResponse.total_count 
        : Object.values(activitiesCounts).reduce((sum: number, count: any) => sum + (count || 0), 0);

      // Process books data
      const booksData = booksResponse.success && booksResponse.data 
        ? booksResponse.data 
        : { total_pages: 0, record_count: 0 };
      const totalPages = booksData.total_pages || 0;
      const recordCount = booksData.record_count || 0;

      // Process meditation data
      const meditationLogs = Array.isArray(meditationResponse) ? meditationResponse : [];
      const totalMeditationMinutes = meditationLogs.reduce((sum: number, log: any) => sum + (log.duration_minutes || 0), 0);
      const totalMeditationHours = totalMeditationMinutes / 60;
      const meditationSessionCount = meditationLogs.length;

      // Process stock transactions data
      const allStockTransactions = stockTransactionsResponse.success && stockTransactionsResponse.transactions 
        ? stockTransactionsResponse.transactions 
        : [];
      
      // Filter transactions by week date range
      const stockTransactionsInWeek = allStockTransactions.filter(transaction => {
        const transactionDateStr = formatDate(new Date(transaction.transaction_date));
        return transactionDateStr >= startDateStr && transactionDateStr <= endDateStr;
      });
      
      const buyCount = stockTransactionsInWeek.filter(t => t.transaction_type === 'buy').length;
      const sellCount = stockTransactionsInWeek.filter(t => t.transaction_type === 'sell').length;
      const totalStockTransactions = stockTransactionsInWeek.length;

      const stats: WeeklyStats = {
        habits: {
          total: habits.length,
          completed: totalHabitCompletions,
          completionRate: totalPossibleCompletions > 0 ? (totalHabitCompletions / totalPossibleCompletions) * 100 : 0,
          habits: habits
        },
        goals: {
          total: goals.length,
          active: activeGoals.length,
          completed: completedGoals.length,
          goals: goals
        },
        tasks: {
          total: tasksCreatedInWeek.length,
          completed: tasksCompletedInWeek.length,
          completionRate: taskCompletionRate,
          tasks: allTasks
        },
        notes: {
          totalDays: totalDays,
          daysWithNotes: daysWithNotes,
          notes: notes
        },
        cashflow: {
          totalSpent,
          totalIncome,
          netCashflow,
          transactions: cashflowEntries.length
        },
        blocks: {
          total: totalBlocks
        },
        lessons: {
          total: totalLessons
        },
        activities: {
          total: totalActivities
        },
        books: {
          totalPages: totalPages,
          recordCount: recordCount
        },
        meditation: {
          totalMinutes: totalMeditationMinutes,
          totalHours: totalMeditationHours,
          sessionCount: meditationSessionCount
        },
        stocks: {
          buyCount: buyCount,
          sellCount: sellCount,
          totalTransactions: totalStockTransactions,
          transactions: stockTransactionsInWeek
        }
      };

      setWeeklyStats(stats);
    } catch (err) {
      console.error('Error loading weekly data:', err);
      setError('Failed to load weekly data');
    } finally {
      loadingDataRef.current = false;
      setLoading(false);
    }
  }, [user?.id]);

  // Sync URL parameter with selectedWeek
  useEffect(() => {
    if (yearWeek !== undefined) {
      if (yearWeek === 'current') {
        // Handle 'current' redirect by navigating to actual current year-week
        const currentYearWeek = getCurrentYearWeek();
        navigate(`/weeks/${currentYearWeek}`, { replace: true });
        return;
      }
      
      const weekOffset = getWeekOffsetFromYearWeek(yearWeek);
      if (weekOffset !== selectedWeek) {
        setSelectedWeek(weekOffset);
      }
      
      // Validate year-week format
      if (!parseYearWeek(yearWeek)) {
        // Invalid year-week format, redirect to current week
        const currentYearWeek = getCurrentYearWeek();
        navigate(`/weeks/${currentYearWeek}`, { replace: true });
      }
    } else {
      // No year-week parameter, redirect to current week
      const currentYearWeek = getCurrentYearWeek();
      navigate(`/weeks/${currentYearWeek}`, { replace: true });
    }
  }, [yearWeek, selectedWeek, navigate]);

  // Load data when component mounts or week changes
  useEffect(() => {
    if (user?.id) {
      Promise.all([
        loadWeeklyData(selectedWeek),
        loadWeeklyNote(selectedWeek)
      ]);
    }
  }, [user, selectedWeek]);

  // Navigation functions
  const goToPreviousWeek = () => {
    const newWeekOffset = selectedWeek - 1;
    const yearWeek = getYearWeekFromOffset(newWeekOffset);
    navigate(`/weeks/${yearWeek}`);
  };

  const goToNextWeek = () => {
    const newWeekOffset = selectedWeek + 1;
    const yearWeek = getYearWeekFromOffset(newWeekOffset);
    navigate(`/weeks/${yearWeek}`);
  };

  const goToCurrentWeek = () => {
    const currentYearWeek = getCurrentYearWeek();
    navigate(`/weeks/${currentYearWeek}`);
  };

  // Get week display info
  const getWeekDisplayInfo = () => {
    const weekDates = getWeekDates(selectedWeek);
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    
    const isCurrentWeek = selectedWeek === 0;
    const weekLabel = isCurrentWeek ? 'This Week' : 
                     selectedWeek === -1 ? 'Last Week' :
                     selectedWeek === 1 ? 'Next Week' :
                     `${Math.abs(selectedWeek)} Week${Math.abs(selectedWeek) > 1 ? 's' : ''} ${selectedWeek < 0 ? 'Ago' : 'Ahead'}`;

    return {
      weekLabel,
      startDate,
      endDate,
      isCurrentWeek
    };
  };

  const weekInfo = getWeekDisplayInfo();

  // Formatting helper functions
  const formatMeditationHours = (hours: number): string => {
    if (hours === 0) return '0 hours';
    // Round to 1 decimal place
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  };

  const formatBookPages = (pages: number, sessions: number): string => {
    if (pages === 0) return '';
    if (sessions > 1) {
      return `read ${pages} pages across ${sessions} reading sessions`;
    }
    return `read ${pages} page${pages !== 1 ? 's' : ''}`;
  };

  // Generate week description paragraph
  const generateWeekDescription = (stats: WeeklyStats | null, weekOffset: number): string => {
    if (!stats) {
      return '';
    }

    const weekDates = getWeekDates(weekOffset);
    const startDate = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDate = weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    const weekIdentifier = yearWeek && yearWeek !== 'current' ? yearWeek : getCurrentYearWeek();
    const totalPossibleHabitCompletions = stats.habits.total * 7;
    
    const parts: string[] = [];
    
    // Week identifier and date range
    parts.push(`This is week ${weekIdentifier}, starting from ${startDate} to ${endDate}.`);
    
    // Habits
    if (totalPossibleHabitCompletions > 0) {
      parts.push(`In this week, you completed ${stats.habits.completed} out of ${totalPossibleHabitCompletions} possible habit completions (${stats.habits.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`In this week, you have ${stats.habits.total} active habit${stats.habits.total !== 1 ? 's' : ''}`);
    }
    
    // Tasks
    if (stats.tasks.total > 0) {
      parts.push(`completed ${stats.tasks.completed} out of ${stats.tasks.total} task${stats.tasks.total !== 1 ? 's' : ''} (${stats.tasks.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`completed 0 tasks`);
    }
    
    // Goals - only mention in_progress goals
    if (stats.goals.active > 0) {
      parts.push(`maintained ${stats.goals.active} active goal${stats.goals.active !== 1 ? 's' : ''}`);
    }
    
    // Notes
    parts.push(`wrote notes on ${stats.notes.daysWithNotes} out of ${stats.notes.totalDays} day${stats.notes.totalDays !== 1 ? 's' : ''}`);
    
    // Cashflow
    if (stats.cashflow.transactions > 0) {
      const cashflowParts: string[] = [];
      if (stats.cashflow.totalIncome > 0) {
        cashflowParts.push(`earned ${stats.cashflow.totalIncome.toLocaleString()}`);
      }
      // Always mention spending if there are transactions
      if (stats.cashflow.totalSpent > 0) {
        cashflowParts.push(`spent ${stats.cashflow.totalSpent.toLocaleString()}`);
      }
      if (cashflowParts.length > 0) {
        if (cashflowParts.length > 1) {
          parts.push(`${cashflowParts.join(' and ')}, with a net cashflow of ${stats.cashflow.netCashflow >= 0 ? '+' : ''}${stats.cashflow.netCashflow.toLocaleString()}`);
        } else {
          parts.push(cashflowParts[0]);
        }
      }
    }
    
    // Blocks
    if (stats.blocks.total > 0) {
      parts.push(`created ${stats.blocks.total} block${stats.blocks.total !== 1 ? 's' : ''}`);
    }
    
    // Lessons
    if (stats.lessons.total > 0) {
      parts.push(`generated ${stats.lessons.total} lesson${stats.lessons.total !== 1 ? 's' : ''}`);
    }
    
    // Activities
    if (stats.activities.total > 0) {
      parts.push(`performed ${stats.activities.total} activit${stats.activities.total !== 1 ? 'ies' : 'y'}`);
    }
    
    // Books
    if (stats.books.totalPages > 0) {
      const bookPagesText = formatBookPages(stats.books.totalPages, stats.books.recordCount);
      if (bookPagesText) {
        parts.push(bookPagesText);
      }
    }
    
    // Meditation
    if (stats.meditation.totalHours > 0) {
      parts.push(`meditated for ${formatMeditationHours(stats.meditation.totalHours)}`);
    }
    
    // Stock transactions
    if (stats.stocks.totalTransactions > 0) {
      const stockParts: string[] = [];
      if (stats.stocks.buyCount > 0) {
        stockParts.push(`bought ${stats.stocks.buyCount} stock${stats.stocks.buyCount !== 1 ? 's' : ''}`);
      }
      if (stats.stocks.sellCount > 0) {
        stockParts.push(`sold ${stats.stocks.sellCount} stock${stats.stocks.sellCount !== 1 ? 's' : ''}`);
      }
      if (stockParts.length > 0) {
        if (stockParts.length > 1) {
          parts.push(stockParts.join(' and '));
        } else {
          parts.push(stockParts[0]);
        }
      }
    }
    
    return parts.join(', ') + '.';
  };

  if (loading) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Weekly Progress</span>}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading weekly data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Weekly Progress</span>}
        />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => loadWeeklyData(selectedWeek)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="week-navigation">
            <button className="nav-btn" onClick={goToPreviousWeek}>←</button>
            <div className="week-info">
              {yearWeek && yearWeek !== 'current' && (
                <p className="year-week-label">{yearWeek}</p>
              )}
            </div>
            <button className="nav-btn" onClick={goToNextWeek}>→</button>
            {!weekInfo.isCurrentWeek && (
              <button className="current-week-btn" onClick={goToCurrentWeek}>
                Current Week
              </button>
            )}
          </div>
        }
        rightActions={
          saving ? (
            <span className="weeks-saving-indicator">
              <div className="saving-spinner"></div>
              Saving...
            </span>
          ) : undefined
        }
      />

      <div className="weeks-content">
        {/* Week Description */}
        {weeklyStats && (
          <div className="week-description">
            {generateWeekDescription(weeklyStats, selectedWeek)}
          </div>
        )}

        {/* Weekly Note Editor */}
        <div className="weekly-note-section">
          <div className="weekly-note-editor">
            {noteLoading ? (
              <div className="note-loading">
                <div className="loading-spinner"></div>
                <p>Loading weekly note...</p>
              </div>
            ) : (
              <BlockEditor
                blockManager={blockManager}
                blocks={contentBlocks}
                placeholder={`Write your notes for ${getWeekDisplayRange(selectedWeek)}...`}
                onSyncStatusChange={() => {}} // Sync status is handled by hook
              />
            )}
          </div>
        </div>
      </div>

      {/* Sync Status Icon */}
      <div className={`weeks-sync-status-icon ${syncStatus}`}>
        {syncStatus === 'syncing' && <div className="weeks-sync-spinner">⟳</div>}
        {syncStatus === 'synced' && <div className="weeks-sync-check">✓</div>}
        {syncStatus === 'error' && <div className="weeks-sync-error">⚠</div>}
      </div>

    </div>
  );
};

export default Weeks;
